import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import {
    filter,
    map,
    skip,
    startWith,
    take,
    takeWhile,
    withLatestFrom
} from 'rxjs/operators';
import { IInvoiceCycle } from '../../../contract-management/IInvoiceCycle';
import * as fromContract from '../../../contract-management/store';
import * as fromDataServices from '../../../data-services';
import { InvoiceContractCycleModel } from '../../../data-services/invoice-contract-cycle';
import * as fromLookups from '../../../lookups';
import * as fromShared from '../../../shared';
import { SelectItem } from '../../../shared/efaact-primeng.module';
import * as fromLoading from '../../../shared/loading/store';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import * as fromUser from '../../../user-info';
import * as fromStore from '../store';
import { Invoice } from '../store/invoice/invoice.model';
// @fromShared.Decorators.withLogging()
@Component({
    selector: 'ef-invoice-management-container',
    templateUrl: './invoice-management-container.component.html',
    styleUrls: ['./invoice-management-container.component.scss'],
    standalone: false
})
export class InvoiceManagementContainerComponent implements OnInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    contracts$: Observable<
        fromDataServices.InvoiceContractCycle.InvoiceContractCycleModel[]
    >;
    invoices$: Observable<Invoice[]>;
    userInfo$: Observable<fromUser.IUserInfo>;
    contractsSelectItems$: Observable<SelectItem[]>;
    chargeNumbers$: Observable<fromStore.ChargeNumber.InvoiceChargeNumber[]>;
    serviceLoading$: Observable<boolean>;
    invoiceCycle$: Observable<IInvoiceCycle[]>;
    invoiceCycleDaysSelectItems$: Observable<SelectItem[]>;
    contractSelectItems$: Observable<SelectItem[]>;
    invoiceCycleDay2SelectItems$: Observable<SelectItem[]>;
    selectedInvoiceContractCycleModel: fromDataServices.InvoiceContractCycle.InvoiceContractCycleModel;
    filterFormGroup: UntypedFormGroup;
    startDate: Date;
    endDate: Date;
    selectedContracts: any = [];
    showInactiveContracts = false;
    showInvoicesOnly = false;
    showInactiveChargeNumbers = false;

    filterChangedSubject = new ReplaySubject<{
        startDate: Date;
        endDate: Date;
        contractList: string[];
        showInactive: boolean;
        showInvoicesOnly: boolean;
    }>(1);
    selectInvoiceSubject = new ReplaySubject<Invoice>(1);
    selectInvoiceCycleSubject = new ReplaySubject<InvoiceContractCycleModel>(1);
    resetInvoiceContract = new ReplaySubject<string>(1);
    saveInvoiceSubject = new ReplaySubject<Invoice[]>(1);
    showInvoicesOnlySubject = new ReplaySubject<boolean>(1);
    selectChargeNumberSubject =
        new ReplaySubject<fromStore.ChargeNumber.InvoiceChargeNumber>(1);
    private selectedInvoice: Invoice;
    maxDate: Date = new Date();
    yearRange: string;
    active = true;
    btnShowDocumentation: ElementRef;
    public tabHeight = '';
    activeState = [true, true, false];
    invoiceContractCycleDataService: EntityCollectionService<fromDataServices.InvoiceContractCycle.InvoiceContractCycleModel>;
    constructor(
        public entityServices: EntityServices,
        private store: Store<fromStore.Invoice.State>,
        private log: LoggingService
    ) {
        this.invoiceContractCycleDataService =
            entityServices.getEntityCollectionService<fromDataServices.InvoiceContractCycle.InvoiceContractCycleModel>(
                fromDataServices.InvoiceContractCycle.entityName
            );
        this.invoiceContractCycleDataService.clearCache();
        this.invoiceContractCycleDataService.getWithQuery({
            activeOnly: true as any
        });

        this.startDate = moment(
            moment().add(-1, 'months').format('MM/01/YYYY')
        ).toDate();
        this.endDate = new Date();
        this.filterFormGroup = new UntypedFormGroup({
            startDate: new UntypedFormControl(this.startDate),
            endDate: new UntypedFormControl(this.endDate),
            contractList: new UntypedFormControl()
        });
    }
    ngOnInit() {
        // this.store.dispatch(fromContract.getContracts({ Activeonly: true }));
        this.store.dispatch(fromContract.getInvoiceCycle());
        this.setupObservables();
        this.setupSubscriptions();
        this.handleOnOpen([0, 1]);
    }
    ngOnDestroy() {
        this.active = false;
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.serviceLoading$ = this.store.pipe(select(fromLoading.getLoading));
        this.contracts$ = this.invoiceContractCycleDataService.entities$.pipe(
            map(
                contracts =>
                    clone(
                        contracts
                    ) as fromDataServices.InvoiceContractCycle.InvoiceContractCycleModel[]
            ),
            map(contracts =>
                contracts.map(contract => ({
                    ...contract,
                    ContractDesc: contract.ContractDesc
                        ? contract.ContractDesc
                        : contract.ContractListID
                }))
            ),
            map(contracts =>
                contracts.sort((a, b) =>
                    a.ContractDesc && b.ContractDesc
                        ? a.ContractDesc.localeCompare(b.ContractDesc)
                        : 0
                )
            )
        );
        this.invoices$ = this.store.pipe(
            takeWhile(() => this.active),
            select(fromStore.Invoice.selectAll),
            // skip(1), // first one is [] from the store being initialized
            // take(1),
            // filter(x => !!x),
            map(x => clone(x))
        );
        this.chargeNumbers$ = this.store.pipe(
            select(fromStore.ChargeNumber.selectAll),
            skip(1) // first one is [] from the store being initialized
        );
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
        this.invoiceCycle$ = this.store.pipe(
            select(fromContract.fromCycle.selectAll)
        );
        this.invoiceCycleDaysSelectItems$ = this.invoiceCycle$.pipe(
            map(items =>
                items.map(x => ({
                    label: x.InvoiceCycleDay,
                    value: x.CycleDayCode
                }))
            )
        );
        this.contractSelectItems$ = this.contracts$.pipe(
            map(items =>
                items.map(x => ({
                    label: x.ContractDesc,
                    value: x.ContractListID
                }))
            )
        );
    }
    setupSubscriptions() {
        this.store
            .pipe(
                takeWhile(() => this.active),
                select(fromLookups.ScheduledDateSelectors.selectMinFiscalYear),
                filter(x => !!x)
            )
            .subscribe(minDate => {
                this.yearRange = `${minDate.getFullYear()}:${moment(new Date())
                    .add(1, 'year')
                    .toDate()
                    .getFullYear()}`;
                this.log.verbose({
                    type: 'year range',
                    yearRange: this.yearRange
                });
            });
        this.invoices$
            .pipe(
                takeWhile(() => this.active),
                filter(invoices => invoices && invoices.length > 0)
            )
            .subscribe(invoices => {
                // this.activeState[1] = true;
                // this.activeState[2] = true;
                // this.handleOnOpen(this.activeState);
                this.selectInvoiceSubject.next(invoices[0]);
            });

        this.showInvoicesOnlySubject
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.filtersChanged();
            });

        combineLatest([
            this.selectInvoiceCycleSubject,
            this.filterChangedSubject
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([invoiceCycle, filters]) => {
                this.log.verbose({
                    type: 'InvoiceManagementContainerComponent:selectInvoiceCycleSubject',
                    invoiceCycle,
                    filters
                });
                this.selectedInvoiceContractCycleModel = invoiceCycle;
                this.selectedContracts = invoiceCycle.ContractListID;
                this.store.dispatch(fromStore.Invoice.clearInvoices());
                this.store.dispatch(
                    fromStore.ChargeNumber.clearInvoiceChargeNumbers()
                );
                this.store.dispatch(
                    fromStore.SessionNumber.clearInvoiceSessionNumbers()
                );
                this.store.dispatch(
                    fromStore.Invoice.postInvoices({
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        contractList: [invoiceCycle.ContractListID],
                        showInactive: filters.showInactive,
                        showInvoicesOnly: filters.showInvoicesOnly
                    })
                );
                this.store.dispatch(
                    fromStore.Invoice.getActiveContracts({
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        showInactive: filters.showInactive
                    })
                );
            });
        this.saveInvoiceSubject
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.filterChangedSubject)
            )
            .subscribe(([items, filters]) => {
                this.store.dispatch(
                    fromStore.Invoice.putInvoices({
                        startDate: this.startDate,
                        endDate: this.endDate,
                        invoices: items
                    })
                );
                this.store.dispatch(
                    fromStore.Invoice.postInvoices({
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        contractList: [items[0].ContractListID],
                        showInactive: filters.showInactive,
                        showInvoicesOnly: filters.showInvoicesOnly
                    })
                );
            });
        combineLatest([this.selectInvoiceSubject, this.filterChangedSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([invoice, filters]) => {
                this.log.verbose({
                    type: 'InvoiceManagementContainerComponent:selectInvoiceSubject',
                    invoice,
                    filters
                });

                this.selectedInvoice = invoice;
                this.store.dispatch(
                    fromStore.ChargeNumber.clearInvoiceChargeNumbers()
                );
                this.store.dispatch(
                    fromStore.ChargeNumber.getInvoiceChargeNumbers({
                        ScheduledStartDate: filters.startDate,
                        ScheduledEndDate: filters.endDate,
                        ContractListID: invoice.ContractListID,
                        ContractDesc: '',
                        showInactive: this.showInactiveChargeNumbers
                    })
                );
                this.store.dispatch(
                    fromStore.SessionNumber.clearInvoiceSessionNumbers()
                );
                this.store.dispatch(
                    fromStore.SessionNumber.getInvoiceSessionNumbers({
                        ScheduledStartDate: filters.startDate,
                        ScheduledEndDate: filters.endDate,
                        ContractListID: [invoice.ContractListID],
                        ContractDesc: [],
                        // showInactive: this.showInactiveChargeNumbers,
                        showInvoicesOnly: filters.showInvoicesOnly,
                        DateRangeView: false
                    })
                );
            });
        combineLatest([
            this.selectChargeNumberSubject,
            this.showInvoicesOnlySubject.pipe(startWith(false))
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([item, invoicesOnly]) => {
                this.log.verbose({
                    type: 'InvoiceManagementContainerComponent:onSelectChargeNumber',
                    item
                });
                this.store.dispatch(
                    fromStore.SessionNumber.getInvoiceSessionNumbers({
                        ScheduledStartDate: item.ScheduledStartDate,
                        ScheduledEndDate: item.ScheduledEndDate,
                        ContractListID: [item.CNListID],
                        ContractDesc: [],
                        // showInactive: this.showInactiveChargeNumbers,
                        showInvoicesOnly: invoicesOnly,
                        DateRangeView: false
                    })
                );
            });

        this.filterFormGroup.controls['startDate'].valueChanges
            .pipe(takeWhile(() => this.active))
            .subscribe(value => {
                if (value) {
                    if (
                        value > this.filterFormGroup.controls['endDate'].value
                    ) {
                        this.filterFormGroup.controls['endDate'].patchValue(
                            value
                        );
                    }
                    this.maxDate = moment(value)
                        .add(12, 'months')
                        .subtract(1, 'days')
                        .toDate();
                }
            });

        this.contractsSelectItems$ = this.contracts$.pipe(
            map(contracts =>
                contracts.map(contract => ({
                    label: contract.ContractDesc,
                    value: contract.ContractListID
                }))
            ),
            map(contracts =>
                contracts.sort((a, b) => a.label.localeCompare(b.label))
            )
        );
        this.resetInvoiceContract
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.contracts$)
            )
            .subscribe(([id, invoices]) => {
                const invoice = invoices.find(x => x.ContractListID === id);
                this.log.verbose({
                    type: 'InvoiceManagementContainerComponent:resetInvoiceContract',
                    invoice
                });
                // this.selectInvoiceCycleSubject.next(invoice);
                this.store.dispatch(fromStore.Invoice.clearInvoices());
                this.store.dispatch(
                    fromStore.Invoice.postUpdateInvoices({
                        startDate: this.startDate,
                        endDate: this.endDate,
                        contractList: [invoice.ContractListID],
                        resetInvoiceSchedule: true
                    })
                );
            });
    }
    datesChanged() {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:datesChanged',
            startDate: this.startDate,
            endDate: this.endDate
        });
        if (
            moment(this.startDate).isValid() &&
            moment(this.endDate).isValid()
        ) {
            this.filtersChanged();
        }
    }
    filtersChanged() {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:filtersChanged',
            startDate: this.startDate,
            endDate: this.endDate,
            selectedContracts: this.selectedContracts,
            showInactiveContracts: this.showInactiveContracts,
            showInvoicesOnly: this.showInvoicesOnly
        });

        if (this.selectedContracts) {
            this.filterChangedSubject.next({
                startDate: this.startDate,
                endDate: this.endDate,
                contractList: Array.isArray(this.selectedContracts)
                    ? this.selectedContracts
                    : [this.selectedContracts],
                showInactive: this.showInactiveContracts,
                showInvoicesOnly: this.showInvoicesOnly
            });
        }
    }
    onSaveInvoice(items: Invoice[]) {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:onSaveItem',
            items
        });
        this.saveInvoiceSubject.next(items);
    }
    onSelectInvoiceCycle(item: InvoiceContractCycleModel) {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:onSelectInvoiceCycle',
            item
        });
        this.store.dispatch(fromStore.Invoice.clearInvoices());
        this.selectInvoiceCycleSubject.next(item);
    }
    onSaveInvoiceCycle(item: InvoiceContractCycleModel[]) {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:onSaveInvoiceCycle',
            item
        });
        if (!item || item.length === 0) {
            return;
        }
        this.invoiceContractCycleDataService
            .upsert(item[0])
            .pipe(take(1))
            .subscribe(saved => {
                setTimeout(() => {
                    this.refreshInvoiceScheduleAfterContractChange(saved);
                }, 0);
            });
    }
    showAllContractsChecked($event) {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:showAllContractsChecked',
            $event
        });
        this.store.dispatch(
            fromStore.Invoice.getActiveContracts({
                startDate: this.startDate,
                endDate: this.endDate,
                showInactive: this.showInactiveContracts
            })
        );
        this.invoiceContractCycleDataService.clearCache();
        this.invoiceContractCycleDataService.getWithQuery({
            activeOnly: !$event.checked as any
        });
        this.filtersChanged();
    }
    showAllCNsChecked($event) {
        this.store.dispatch(
            fromStore.ChargeNumber.getInvoiceChargeNumbers({
                ScheduledStartDate: this.startDate,
                ScheduledEndDate: this.endDate,
                ContractListID: this.selectedInvoice.ContractListID,
                ContractDesc: '',
                showInactive: this.showInactiveChargeNumbers
            })
        );
    }
    handleOnOpen($event: number[]) {
        // this is the best way to handle accordion open/close
        // baseheight may need adjustment depending on anything outside the tabs
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:handleOnOpen',
            $event,
            activeState: this.activeState
        });
        const baseHeight = 73;
        this.tabHeight = `${
            baseHeight / ($event && $event.length ? $event.length : 1) - 1
        }vh`;
    }
    onSelectedChargeNumberTab($event) {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:onSelectedChargeNumberTab',
            $event,
            activeState: this.activeState
        });
        if ($event === true) {
            this.activeState[1] = true;
            const event = [];
            if (this.activeState[0]) {
                event.push(0);
            }
            if (this.activeState[1]) {
                event.push(1);
            }
            if (this.activeState[2]) {
                event.push(2);
            }
            setTimeout(() => {
                this.handleOnOpen(event);
            }, 0);
        }
    }
    resetInvoiceSchedule() {
        this.log.verbose({
            type: 'InvoiceManagementContainerComponent:resetInvoiceSchedule'
        });
        this.resetInvoiceContract.next();
    }

    private refreshInvoiceScheduleAfterContractChange(
        updated: InvoiceContractCycleModel
    ) {
        if (
            !updated ||
            !this.selectedInvoiceContractCycleModel ||
            this.selectedInvoiceContractCycleModel.ContractListID !==
                updated.ContractListID
        ) {
            return;
        }
        this.selectedInvoiceContractCycleModel = updated;
        this.store.dispatch(fromStore.Invoice.clearInvoices());
        this.store.dispatch(
            fromStore.Invoice.postInvoices({
                startDate: this.startDate,
                endDate: this.endDate,
                contractList: [updated.ContractListID],
                showInactive: this.showInactiveContracts,
                showInvoicesOnly: this.showInvoicesOnly
            })
        );
    }
}
